import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  ToolsService,
  WellDto,
  WellUpdateProjectDto,
  ChangeProjectWellRequest,
  UpdateWellInfoRequest
} from '../../services/tools.service';

@Component({
  selector: 'app-well-update',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule],
  templateUrl: './well-update.component.html',
  styleUrls: ['./well-update.component.css']
})
export class WellUpdateComponent implements OnInit {

  // ── Shared ─────────────────────────────────────────────────────────────
  wells: WellDto[] = [];
  isLoadingWells = false;

  // ── Section 1: Project Info / Change Well ──────────────────────────────
  years: number[] = [];
  selectedYear: number = new Date().getFullYear();

  projects: WellUpdateProjectDto[] = [];
  isLoadingProjects = false;
  selectedProject: WellUpdateProjectDto | null = null;

  // Wells filtered to the same BU as the project's current well
  changeWells: WellDto[] = [];
  projectSelectedWell: WellDto | null = null;
  projectWellBuName: string = '';

  isChangingWell = false;
  changeWellSuccess: string | null = null;
  changeWellError: string | null = null;

  // ── Section 2: Well Info / Update Well Info ────────────────────────────
  selectedWell: WellDto | null = null;
  wellInfoBuName: string = '';
  wellName: string = '';
  wellLocation: string = '';

  isUpdatingWellInfo = false;
  wellInfoSuccess: string | null = null;
  wellInfoError: string | null = null;

  constructor(private toolsService: ToolsService, public dialogRef: MatDialogRef<WellUpdateComponent>) {}


  ngOnInit(): void {
    this.buildYears();
    this.loadWells();
  }

  // ── Initialisation helpers ─────────────────────────────────────────────

  private buildYears(): void {
    const current = new Date().getFullYear();
    this.years = [current, current - 1];
    this.selectedYear = current;
  }

  private loadWells(): void {
    this.isLoadingWells = true;
    this.toolsService.getWells().subscribe({
      next: (data) => {
        this.wells = data;
        this.isLoadingWells = false;
        // Pre-select first well for Section 2
        if (this.wells.length) this.onWellInfoWellChange(this.wells[0]);
        // Load projects for default year
        this.loadProjectsByYear(this.selectedYear);
      },
      error: () => { this.isLoadingWells = false; }
    });
  }

  private loadProjectsByYear(year: number): void {
    this.isLoadingProjects = true;
    this.projects = [];
    this.selectedProject = null;
    this.changeWells = [];
    this.projectSelectedWell = null;
    this.projectWellBuName = '';

    this.toolsService.getProjectsByYear(year).subscribe({
      next: (data) => {
        this.projects = data;
        this.isLoadingProjects = false;
        if (this.projects.length) this.onProjectChange(this.projects[0]);
      },
      error: () => { this.isLoadingProjects = false; }
    });
  }

  // ── Section 1 event handlers ───────────────────────────────────────────

  onYearChange(year: number): void {
    this.selectedYear = year;
    this.changeWellSuccess = null;
    this.changeWellError = null;
    this.loadProjectsByYear(year);
  }

  onProjectChange(project: WellUpdateProjectDto): void {
    this.selectedProject = project;
    this.changeWellSuccess = null;
    this.changeWellError = null;

    // Find the current well for this project
    const currentWell = this.wells.find(w => w.id === project.wellId) ?? null;
    this.projectSelectedWell = currentWell;
    this.projectWellBuName = currentWell?.sbuName ?? currentWell?.sbuCode ?? '';

    // Filter wells list to the same BU so the user picks a valid replacement
    this.changeWells = currentWell
      ? this.wells.filter(w => w.sbuCode === currentWell.sbuCode)
      : this.wells;
  }

  changeWell(): void {
    if (!this.selectedProject || !this.projectSelectedWell) return;
    this.changeWellSuccess = null;
    this.changeWellError = null;
    this.isChangingWell = true;

    const req: ChangeProjectWellRequest = { wellId: this.projectSelectedWell.id };
    this.toolsService.changeProjectWell(this.selectedProject.id, req).subscribe({
      next: () => {
        this.changeWellSuccess = 'Well updated successfully.';
        this.isChangingWell = false;
      },
      error: (err: HttpErrorResponse) => {
        this.changeWellError = err.error?.detail ?? 'Unable to update Well.';
        this.isChangingWell = false;
      }
    });
  }

  // ── Section 2 event handlers ───────────────────────────────────────────

  onWellInfoWellChange(well: WellDto): void {
    this.selectedWell = well;
    this.wellInfoBuName = well.sbuName ?? well.sbuCode ?? '';
    this.wellName = well.name ?? '';
    this.wellLocation = well.wellLocation ?? '';
    this.wellInfoSuccess = null;
    this.wellInfoError = null;
  }

  get isWellInfoValid(): boolean {
    return !!this.wellName.trim() && !!this.wellLocation.trim();
  }

  updateWellInfo(): void {
    if (!this.selectedWell || !this.isWellInfoValid) return;
    this.wellInfoSuccess = null;
    this.wellInfoError = null;
    this.isUpdatingWellInfo = true;

    const req: UpdateWellInfoRequest = {
      wellName: this.wellName.trim(),
      wellLocation: this.wellLocation.trim(),
      sbuCode: this.selectedWell.sbuCode
    };
    this.toolsService.updateWellInfo(this.selectedWell.id, req).subscribe({
      next: () => {
        this.wellInfoSuccess = 'Well Info updated successfully.';
        // Refresh the wells list so the new name shows in dropdowns
        const updatedWell = { ...this.selectedWell!, name: req.wellName, wellLocation: req.wellLocation };
        this.wells = this.wells.map(w => w.id === updatedWell.id ? updatedWell : w);
        this.selectedWell = updatedWell;
        this.isUpdatingWellInfo = false;
      },
      error: (err: HttpErrorResponse) => {
        this.wellInfoError = err.error?.detail ?? 'Unable to update Well Info.';
        this.isUpdatingWellInfo = false;
      }
    });
  }
}
